import React, { CSSProperties, FC, useState, useRef, useEffect } from "react"
import { renderRichText } from "gatsby-source-contentful/rich-text"
import xss from "xss"

import { AccordionBlockFragmentFragment } from "@graphqlTypes"

import { BREAKPOINTS } from "@constants"

import {
  convertStringToSlug,
  generateConditionalClassName,
  isClientSide,
  preventSleepyBuddies,
  smoothScrollToElement,
} from "@utils"

import Media from "@components/lp/Media/Media"
import TextLink from "@components/TextLink/TextLink"

import { MEDIA_POSITIONS } from "../../AccordionBlock"

import Observer from "@components/Observer/Observer"

import "./AccordionListWithImages.scss"

type AccordionListWithImagesProps = {
  items: AccordionBlockFragmentFragment["items"]
  title: string
  mediaPosition?: MEDIA_POSITIONS
  useAutoAdvance?: boolean | null
}

const AccordionListWithImages: FC<AccordionListWithImagesProps> = ({
  items = [],
  title,
  mediaPosition = MEDIA_POSITIONS.DEFAULT,
  useAutoAdvance,
}) => {
  if (!items) return null

  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)
  const [activeTabsIndexes, setActiveTabsIndexes] = useState<number[]>([0])
  const [height, setHeight] = useState<null | number>(null)
  const TabsRef = useRef<HTMLDivElement | null>(null)
  const id = `${convertStringToSlug(title)}`

  const calculateHeight = () => {
    if (window.innerWidth < BREAKPOINTS.L) {
      setHeight(null)
    } else {
      setHeight(TabsRef.current?.getBoundingClientRect().height ?? null)
    }
  }

  const handleClickButton = (index: number) => {
    calculateHeight()

    if (window.innerWidth < BREAKPOINTS.L) {
      setActiveTabsIndexes(indexes => indexes.includes(index) ? indexes.filter(i => i !== index) : [...indexes, index])
    } else {
      setActiveTabIndex(index)
    }
  }

  useEffect(() => {
    if (!isClientSide()) return

    const mobileCheck = () => {
      setIsMobile(window.innerWidth < BREAKPOINTS.L)
      calculateHeight()
    }

    window.addEventListener("resize", mobileCheck)

    mobileCheck()

    return () => {
      window.removeEventListener("resize", mobileCheck)
    }
  }, [])

  useEffect(() => {
    if (!useAutoAdvance || !isClientSide() || window.innerWidth < BREAKPOINTS.L) return

    const tabsEls = TabsRef.current?.querySelectorAll<HTMLDivElement>(".AccordionListWithImages--TabContent")
    if (!tabsEls) return

    const handleAnimationEnd = () => {
      setActiveTabIndex(
        _activeTabIndex =>
          _activeTabIndex + 1 >= items.length ?
            0 :
            _activeTabIndex + 1
      )
    }

    tabsEls.forEach(tabEl => {
      tabEl.addEventListener("animationend", handleAnimationEnd)
    })

    return () => {
      tabsEls.forEach(tabEl => {
        tabEl.removeEventListener("animationend", handleAnimationEnd)
      })
    }
  }, [isMobile])

  return (
    <Observer>
      {(ref, isObserved) => (
        <div ref={ref}>
          <div
            className={generateConditionalClassName({
              "AccordionListWithImages": true,
              "has-title": !!title,
              "use-auto-advance": !!useAutoAdvance,
              "is-observed": isObserved,
              [`media-position--${mediaPosition}`]: !!mediaPosition,
            })}
            ref={TabsRef}
            style={{ height } as CSSProperties}
          >
            <div className="AccordionListWithImages--Tabs">
              {title && (
                <h3>{title}</h3>
              )}
              <div>
                {items.map((tab, i) => {
                  if (!tab) return null
                  return (
                    <div
                      key={tab.title}
                      className={generateConditionalClassName({
                        "is-active": isMobile ? activeTabsIndexes.includes(i) : activeTabIndex === i,
                        "is-inactive": isMobile ? !activeTabsIndexes.includes(i) : activeTabIndex !== i,
                      })}
                      id={`${id}-${i}`}
                    >
                      <div className="AccordionListWithImages--TabContent">
                        <button
                          className="Accordion--title-button"
                          onClick={() => handleClickButton(i)}
                        >
                          <h4
                            className="Accordion--title-button-h4"
                            dangerouslySetInnerHTML={{
                              __html: preventSleepyBuddies(xss(tab.title as string)),
                            }}
                          />
                        </button>
                        <div className="content">
                          {tab.content?.raw && renderRichText({ raw: tab.content.raw, references: [] })}
                          {tab.textLinkText && tab.textLinkUrl && (
                            <TextLink href={tab.textLinkUrl}>{tab.textLinkText}</TextLink>
                          )}
                        </div>
                      </div>
                      {(tab.image || tab.wistiaId) && (
                        <div className="AccordionListWithImages--TabImage mobile">
                          <Media
                            image={tab.image}
                            includeMediaBoxShadow={!!tab.includeMediaBoxShadow}
                            playVideoInline={tab.playVideoInline}
                            playVideoInPnpPlayer={tab.playVideoInPnpPlayer}
                            wistiaId={tab.wistiaId}
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="AccordionListWithImages--Desktop">
              {items.map((tab, i) => (tab && (tab.image || tab.wistiaId)) ? (
                <div
                  className="AccordionListWithImages--TabImage desktop"
                  style={{ opacity: i === activeTabIndex ? 1 : 0 }}
                >
                  <Media
                    image={tab.image}
                    includeMediaBoxShadow={!!tab.includeMediaBoxShadow}
                    playVideoInline={tab.playVideoInline}
                    wistiaId={tab.wistiaId}
                  />
                </div>
              ) : null)}
            </div>
          </div>
        </div>
      )}
    </Observer>
  )
}

export default AccordionListWithImages
